import {
  ADAPTER_BASKET_REQUEST_IDS,
  ADAPTER_BASKET_REQUESTS,
  BasketCategoryModel,
  BasketChannelModel,
  BasketColumnModel,
  BasketLabelModel,
  BasketModel,
} from '@agent/core/basket/basket.model';
import { BasketCategoryType } from '@agent/core/basket/models/basket';
import { EpticaRequestType } from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { copy } from 'angular';

import { ChannelType, EpticaFields, Memo, RequestFields, Skill } from '@contact/upgrade';

export enum PriorityType {
  NORMAL = 'NORMAL',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export interface RequestModel extends BasketModel {
  readonly id: string;
  readonly creationDate: Date | undefined;
  readonly sendDate: Date | undefined;
  readonly object: string;
  readonly content: string;
  readonly hasAttachment: boolean;
  readonly contactName: string;
  readonly types: EpticaRequestType[];
  readonly skillList: any;
  readonly memo: Memo;
  readonly priority: PriorityType;
  readonly recipient: string;
  readonly reason: string;
  readonly state: 'open' | 'closed';
  readonly lastEventDate: Date;
  readonly transferMemo: Memo;
  readonly hasDraft: boolean;
  readonly draftUuid?: string;
  readonly customerId?: string;
  readonly customerEmail?: string;
  readonly bookmark: boolean;
  readonly lastContactEmail: string | undefined;
  readonly isLastContactPrincipalContact: boolean;
  readonly isCurrentCsrResponsible: boolean;
  readonly read: boolean;
}

export interface QueryFilterModel {
  // if filter is present
  readonly hasFilters: boolean;
  // subject filter
  readonly subject: string;
  // domain nam filter
  readonly domain: string;
  // skill list filter
  readonly skills: Skill[];
  // if filter attachment is enabled
  readonly attachmentEnabled: boolean;
  // if request has attachment
  readonly attachmentActive: boolean;
  // priorities to look for
  readonly priorities: PriorityType[];
  // period filter begin time
  readonly bookmark: boolean;
  readonly beginTime: Date;
  // period filter end time
  readonly endTime: Date;
  // if period filter enabled
  readonly isPeriodFilterEnabled: boolean;
  // destination email filter
  readonly recipient: string;
  // reason filter
  readonly reason: string;
  // request id filter
  readonly requestId: string;
}

/**
 * Quick filters are made for elements that are located below the "FILTER" panel on the different mail baskets.
 * They are not linked with the actual filters themselves and are volatile (not saved to the database).
 */
export interface QuickFilterModel {
  // Selected team in the 'common' basket type
  readonly teamId: string;
  // Selected delegator csr id in the 'delegated' basket type
  readonly delegatorCsrId: string;
  // Selected team in the supervisor basket ('personal_for_csr' basket type)
  readonly supervisedTeamId: string;
  // Selected csr in the supervisor basket ('personal_for_csr' basket type)
  readonly supervisedCsrId: string;
  // period filter begin time in sent mails basket
  readonly beginTime: Date;
  // period filter end time in sent mails basket
  readonly endTime: Date;
  // enabled periode filter in sent mails basket
  readonly isSendPeriodFilterEnabled: boolean;
  /**
   * if this filter is applied
   * see ODECSCNGACCEPTANCE-16676 : nbTotalRequests depends on a boolean meaning whether a filter is applied or not)
   */
  readonly hasFilters: boolean;
}

export interface DelegatorCsrModel {
  readonly id: string;
  readonly name: string;
  readonly sortName: string;
}

export interface SupervisedCsrModel {
  readonly name: string;
  readonly id: string;
  readonly teamId: string;
}

export interface TeamModel {
  readonly id: string;
  readonly name: string;
}

export interface SearchDto {
  // uuid of agent
  readonly csrUuid: string;
  // mail user linked to the agent account id
  readonly accountId: string;
  // mail user account group path
  readonly accountGroupPath: string;
}

export interface MailColumnModel extends BasketColumnModel {
  // colunm must be display label
  readonly showLabel: boolean;
  // is visible if profile agent has features supervisor enabled
  readonly visibleForSup: boolean;
  readonly visibleForAgent: boolean;
}

export interface MailCategoryModel extends BasketCategoryModel<MailColumnModel> {}

export interface MailLabelModel extends BasketLabelModel<RequestModel> {
  // filter label
  readonly labelName: string;
  // is visible if profile agent has features supervisor enabled
  readonly visibleForSup: boolean;
  readonly visibleForAgent: boolean;
}

export interface MailChannelModel extends BasketChannelModel<MailLabelModel, MailCategoryModel> {
  // filter query
  readonly queryFilters: QueryFilterModel;
  readonly quickFilters: QuickFilterModel;
}

export const INITIAL_STATE_QUERY_FILTER: QueryFilterModel = {
  hasFilters: false,
  subject: '',
  requestId: '',
  domain: '',
  skills: [],
  recipient: '',
  attachmentEnabled: false,
  attachmentActive: false,
  priorities: [],
  bookmark: false,
  beginTime: new Date(),
  endTime: new Date(),
  isPeriodFilterEnabled: false,
  reason: '',
};

export const INITIAL_STATE_QUICK_FILTER: QuickFilterModel = {
  teamId: '',
  delegatorCsrId: '',
  supervisedTeamId: '',
  supervisedCsrId: '',
  beginTime: new Date(),
  endTime: new Date(),
  isSendPeriodFilterEnabled: false,
  hasFilters: false,
};

/**
 * initial state for personal category
 */
const INITIAL_STATE_CATEGORY_PERSONAL: MailCategoryModel = {
  category: BasketCategoryType.PERSONAL,
  lastLabelSelected: EpticaRequestType.RELATED,
  sort: {
    defaultKey: RequestFields.WEIGHT,
    key: RequestFields.WEIGHT,
    order: 'desc',
  },
  columns: [
    {
      label: 'priority',
      sortKey: RequestFields.WEIGHT,
      showLabel: false,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'bookmark',
      showLabel: false,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'date_creation',
      sortKey: EpticaFields.CREATION_DATE,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'contactName',
      sortKey: EpticaFields.CUSTOMER_LASTNAME,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'recipient',
      sortKey: RequestFields.RECIPIENT,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'skillList',
      sortKey: RequestFields.SKILL,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'reason',
      sortKey: RequestFields.OWI_REASON,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'object',
      sortKey: EpticaFields.SUBJECT,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'type',
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
  ],
};

/**
 * initial state for personal category
 */
const INITIAL_STATE_CATEGORY_PERSONAL_FOR_CSR: MailCategoryModel = {
  category: BasketCategoryType.PERSONAL_FOR_CSR,
  lastLabelSelected: EpticaRequestType.RELATED_FOR_SUP_CSR,
  sort: {
    defaultKey: RequestFields.WEIGHT,
    key: RequestFields.WEIGHT,
    order: 'desc',
  },
  columns: [
    {
      label: 'priority',
      sortKey: RequestFields.WEIGHT,
      showLabel: false,
      visibleForSup: true,
      visibleForAgent: false,
    },
    {
      label: 'bookmark',
      showLabel: false,
      visibleForSup: true,
      visibleForAgent: false,
    },
    {
      label: 'date_creation',
      sortKey: EpticaFields.CREATION_DATE,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: false,
    },
    {
      label: 'contactName',
      sortKey: EpticaFields.CUSTOMER_LASTNAME,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: false,
    },
    {
      label: 'recipient',
      sortKey: RequestFields.RECIPIENT,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: false,
    },
    {
      label: 'skillList',
      sortKey: RequestFields.SKILL,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: false,
    },
    {
      label: 'reason',
      sortKey: RequestFields.OWI_REASON,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: false,
    },
    {
      label: 'object',
      sortKey: EpticaFields.SUBJECT,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: false,
    },
    {
      label: 'type',
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: false,
    },
  ],
};

/**
 * initial state for delegated requests
 */
const INITIAL_STATE_CATEGORY_DELEGATED: MailCategoryModel = {
  ...INITIAL_STATE_CATEGORY_PERSONAL,
  category: BasketCategoryType.DELEGATED,
  lastLabelSelected: EpticaRequestType.DELEGATED,
};

const INITIAL_STATE_CATEGORY_COMMON: MailCategoryModel = {
  category: BasketCategoryType.COMMON,
  lastLabelSelected: EpticaRequestType.ASSIGNABLE,
  sort: {
    defaultKey: RequestFields.WEIGHT,
    key: RequestFields.WEIGHT,
    order: 'desc',
  },
  columns: [
    {
      label: 'priority',
      sortKey: RequestFields.WEIGHT,
      showLabel: false,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'bookmark',
      showLabel: false,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'date_creation',
      sortKey: EpticaFields.CREATION_DATE,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'contactName',
      sortKey: EpticaFields.CUSTOMER_LASTNAME,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'recipient',
      sortKey: RequestFields.RECIPIENT,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'skillList',
      sortKey: RequestFields.SKILL,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'reason',
      sortKey: RequestFields.OWI_REASON,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'object',
      sortKey: EpticaFields.SUBJECT,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
  ],
};

/**
 * initial state for draft category
 */
const INITIAL_STATE_CATEGORY_DRAFT: MailCategoryModel = {
  category: BasketCategoryType.DRAFT,
  lastLabelSelected: EpticaRequestType.DRAFT,
  sort: {
    defaultKey: RequestFields.WEIGHT,
    key: RequestFields.WEIGHT,
    order: 'desc',
  },
  columns: [
    {
      label: 'priority',
      sortKey: RequestFields.WEIGHT,
      showLabel: false,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'date_creation',
      sortKey: EpticaFields.CREATION_DATE,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'contactName',
      sortKey: EpticaFields.CUSTOMER_LASTNAME,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'recipient',
      sortKey: RequestFields.RECIPIENT,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'skillList',
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'reason',
      sortKey: RequestFields.OWI_REASON,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'object',
      sortKey: EpticaFields.SUBJECT,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'type',
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
  ],
};

/**
 * initial state for all category
 */
const INITIAL_STATE_CATEGORY_ALL: MailCategoryModel = {
  category: BasketCategoryType.DEDICATED_AGENT_UNASSIGNED,
  lastLabelSelected: EpticaRequestType.DEDICATED_AGENT_UNASSIGNED,
  sort: {
    defaultKey: EpticaFields.CREATION_DATE,
    key: EpticaFields.CREATION_DATE,
    order: 'desc',
  },
  columns: [
    {
      label: 'priority',
      sortKey: RequestFields.WEIGHT,
      showLabel: false,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'bookmark',
      showLabel: false,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'date_creation',
      sortKey: EpticaFields.CREATION_DATE,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'contactName',
      sortKey: EpticaFields.CUSTOMER_LASTNAME,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'recipient',
      sortKey: RequestFields.RECIPIENT,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'skillList',
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'reason',
      sortKey: RequestFields.OWI_REASON,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'object',
      sortKey: EpticaFields.SUBJECT,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'state',
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
  ],
};

/**
 * initial state for sent category
 */
const INITIAL_STATE_CATEGORY_SENT: MailCategoryModel = {
  category: BasketCategoryType.SENT,
  lastLabelSelected: EpticaRequestType.SENT,
  sort: {
    defaultKey: RequestFields.WEIGHT,
    key: RequestFields.WEIGHT,
    order: 'desc',
  },
  columns: [
    {
      label: 'priority',
      sortKey: RequestFields.WEIGHT,
      showLabel: false,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'bookmark',
      showLabel: false,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'date_creation',
      sortKey: EpticaFields.CREATION_DATE,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'date_send',
      sortKey: EpticaFields.EVENT_DATE,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'contactName',
      sortKey: EpticaFields.CUSTOMER_LASTNAME,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'recipient',
      sortKey: RequestFields.RECIPIENT,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'skillList',
      showLabel: true,
      sortKey: RequestFields.SKILL,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'reason',
      sortKey: RequestFields.OWI_REASON,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
    {
      label: 'object',
      sortKey: EpticaFields.SUBJECT,
      showLabel: true,
      visibleForSup: true,
      visibleForAgent: true,
    },
  ],
};

/**
 * initial state for related label
 */
const INITIAL_STATE_LABEL_RELATED: MailLabelModel = {
  label: EpticaRequestType.RELATED,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 1,
  labelName: 'agent:contact.workspace.basket.all',
  visibleForSup: true,
  visibleForAgent: true,
  showPagination: true,
};

/**
 * initial state for dedicated agent unread label
 */
const INITIAL_STATE_LABEL_DEDICATED_AGENT_UNREAD: MailLabelModel = {
  label: EpticaRequestType.DEDICATED_AGENT_UNREAD,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 2,
  labelName: 'agent:contact.workspace.basket.dedicated_agent_unread',
  visibleForSup: true,
  visibleForAgent: true,
  showPagination: true,
};

/**
 * initial state for transfered label
 */
const INITIAL_STATE_LABEL_TRANSFERED: MailLabelModel = {
  label: EpticaRequestType.TRANSFERED,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 3,
  labelName: 'agent:contact.workspace.basket.transfers',
  visibleForSup: true,
  visibleForAgent: true,
  showPagination: true,
};

/**
 * initial state for transfered label
 */
const INITIAL_STATE_LABEL_APPROVAL: MailLabelModel = {
  label: EpticaRequestType.APPROVAL,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 4,
  labelName: 'agent:contact.workspace.basket.approval',
  visibleForSup: true,
  visibleForAgent: false,
  showPagination: true,
};

/**
 * initial state for approval rejected label
 */
const INITIAL_STATE_LABEL_APPROVAL_IN_PROCESS: MailLabelModel = {
  label: EpticaRequestType.APPROVAL_IN_PROCESS,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 5,
  labelName: 'agent:contact.workspace.basket.approval_in_process',
  visibleForSup: false,
  visibleForAgent: true,
  showPagination: true,
};

/**
 * initial state for approval rejected label
 */
const INITIAL_STATE_LABEL_APPROVAL_REJECTED: MailLabelModel = {
  label: EpticaRequestType.APPROVAL_REJECTED,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 6,
  labelName: 'agent:contact.workspace.basket.rejected_approval',
  visibleForSup: false,
  visibleForAgent: true,
  showPagination: true,
};

/**
 * initial state for ask for help sent label
 */
const INITIAL_STATE_LABEL_ASK_FOR_HELP_SENT: MailLabelModel = {
  label: EpticaRequestType.ASK_FOR_HELP_SENT,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 7,
  labelName: 'agent:contact.workspace.basket.sent_askForHelp',
  visibleForSup: true,
  visibleForAgent: true,
  showPagination: true,
};

/**
 * initial state for ask for help received label
 */
const INITIAL_STATE_LABEL_ASK_FOR_HELP_RECEIVED: MailLabelModel = {
  label: EpticaRequestType.ASK_FOR_HELP_RECEIVED,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 8,
  labelName: 'agent:contact.workspace.basket.received_askForHelp',
  visibleForSup: true,
  visibleForAgent: true,
  showPagination: true,
};

/**
 * initial state for ask for help answered label
 */
const INITIAL_STATE_LABEL_ASK_FOR_HELP_ANSWERED: MailLabelModel = {
  label: EpticaRequestType.ASK_FOR_HELP_ANSWERED,
  category: BasketCategoryType.PERSONAL,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 9,
  labelName: 'agent:contact.workspace.basket.answered_askForHelp',
  visibleForSup: true,
  visibleForAgent: true,
  showPagination: true,
};

/**
 * initial state for related for other csr
 */
const INITIAL_STATE_LABEL_RELATED_FOR_OTHER_CSR: MailLabelModel = {
  label: EpticaRequestType.RELATED_FOR_SUP_CSR,
  category: BasketCategoryType.PERSONAL_FOR_CSR,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 1,
  labelName: 'agent:contact.workspace.basket.all',
  visibleForSup: true,
  visibleForAgent: false,
  showPagination: true,
};

/**
 * initial state for related label
 */
const INITIAL_STATE_LABEL_DEDICATED_AGENT_UNASSIGNED: MailLabelModel = {
  label: EpticaRequestType.DEDICATED_AGENT_UNASSIGNED,
  category: BasketCategoryType.DEDICATED_AGENT_UNASSIGNED,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 1,
  labelName: 'agent:contact.workspace.basket.all',
  visibleForSup: true,
  visibleForAgent: true,
  showPagination: true,
};

/**
 * initial state for assignable label
 */
const INITIAL_STATE_LABEL_ASSIGNABLE: MailLabelModel = {
  label: EpticaRequestType.ASSIGNABLE,
  category: BasketCategoryType.COMMON,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 1,
  labelName: 'commons:basic.all',
  visibleForSup: true,
  visibleForAgent: true,
  showPagination: true,
};

/**
 * initial state for related for sup label
 */
const INITIAL_STATE_LABEL_SENT: MailLabelModel = {
  label: EpticaRequestType.SENT,
  category: BasketCategoryType.SENT,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 1,
  labelName: 'agent:contact.workspace.basket.sent',
  visibleForSup: true,
  visibleForAgent: true,
  showPagination: true,
};

/**
 * initial state for related for sup label
 */
const INITIAL_STATE_LABEL_DRAFT: MailLabelModel = {
  label: EpticaRequestType.DRAFT,
  category: BasketCategoryType.DRAFT,
  timestamp: 0,
  nbTotalRequests: 0,
  nbRequests: 0,
  requests: ADAPTER_BASKET_REQUESTS.getInitialState(),
  requestsInError: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  requestsSelected: ADAPTER_BASKET_REQUEST_IDS.getInitialState(),
  currentPage: 1,
  order: 1,
  labelName: 'agent:contact.workspace.basket.draft',
  visibleForSup: true,
  visibleForAgent: true,
  showPagination: false,
};

/**
 * Initial state for delegated label.
 */
const INITIAL_STATE_LABEL_DELEGATED: MailLabelModel = {
  ...INITIAL_STATE_LABEL_RELATED,
  label: EpticaRequestType.DELEGATED,
  category: BasketCategoryType.DELEGATED,
  labelName: 'agent:contact.workspace.basket.delegated_mails',
};

/**
 * initial object value when store initializing
 */
export const INITIAL_STATE: MailChannelModel = {
  channel: ChannelType.MAIL,
  loading: false,
  pageSize: 10,
  categorySelected: BasketCategoryType.PERSONAL,
  categories: {
    ids: [
      BasketCategoryType.PERSONAL,
      BasketCategoryType.COMMON,
      BasketCategoryType.DRAFT,
      BasketCategoryType.DEDICATED_AGENT_UNASSIGNED,
      BasketCategoryType.PERSONAL_FOR_CSR,
      BasketCategoryType.SENT,
      BasketCategoryType.DELEGATED,
    ],
    entities: {
      [BasketCategoryType.PERSONAL]: INITIAL_STATE_CATEGORY_PERSONAL,
      [BasketCategoryType.COMMON]: INITIAL_STATE_CATEGORY_COMMON,
      [BasketCategoryType.DRAFT]: INITIAL_STATE_CATEGORY_DRAFT,
      [BasketCategoryType.DEDICATED_AGENT_UNASSIGNED]: INITIAL_STATE_CATEGORY_ALL,
      [BasketCategoryType.PERSONAL_FOR_CSR]: INITIAL_STATE_CATEGORY_PERSONAL_FOR_CSR,
      [BasketCategoryType.SENT]: INITIAL_STATE_CATEGORY_SENT,
      [BasketCategoryType.DELEGATED]: INITIAL_STATE_CATEGORY_DELEGATED,
    },
  },
  labelSelected: EpticaRequestType.RELATED,
  labels: {
    ids: [
      EpticaRequestType.RELATED,
      EpticaRequestType.RELATED_FOR_SUP_CSR,
      EpticaRequestType.DEDICATED_AGENT_UNREAD,
      EpticaRequestType.TRANSFERED,
      EpticaRequestType.APPROVAL,
      EpticaRequestType.APPROVAL_IN_PROCESS,
      EpticaRequestType.APPROVAL_REJECTED,
      EpticaRequestType.ASK_FOR_HELP_SENT,
      EpticaRequestType.ASK_FOR_HELP_RECEIVED,
      EpticaRequestType.ASK_FOR_HELP_ANSWERED,
      EpticaRequestType.ASSIGNABLE,
      EpticaRequestType.DEDICATED_AGENT_UNASSIGNED,
      EpticaRequestType.DRAFT,
      EpticaRequestType.SENT,
      EpticaRequestType.DELEGATED,
    ],
    entities: {
      [EpticaRequestType.RELATED]: INITIAL_STATE_LABEL_RELATED,
      [EpticaRequestType.RELATED_FOR_SUP_CSR]: INITIAL_STATE_LABEL_RELATED_FOR_OTHER_CSR,
      [EpticaRequestType.DEDICATED_AGENT_UNREAD]: INITIAL_STATE_LABEL_DEDICATED_AGENT_UNREAD,
      [EpticaRequestType.DEDICATED_AGENT_UNASSIGNED]: INITIAL_STATE_LABEL_DEDICATED_AGENT_UNASSIGNED,
      [EpticaRequestType.TRANSFERED]: INITIAL_STATE_LABEL_TRANSFERED,
      [EpticaRequestType.APPROVAL]: INITIAL_STATE_LABEL_APPROVAL,
      [EpticaRequestType.APPROVAL_IN_PROCESS]: INITIAL_STATE_LABEL_APPROVAL_IN_PROCESS,
      [EpticaRequestType.APPROVAL_REJECTED]: INITIAL_STATE_LABEL_APPROVAL_REJECTED,
      [EpticaRequestType.ASK_FOR_HELP_SENT]: INITIAL_STATE_LABEL_ASK_FOR_HELP_SENT,
      [EpticaRequestType.ASK_FOR_HELP_RECEIVED]: INITIAL_STATE_LABEL_ASK_FOR_HELP_RECEIVED,
      [EpticaRequestType.ASK_FOR_HELP_ANSWERED]: INITIAL_STATE_LABEL_ASK_FOR_HELP_ANSWERED,
      [EpticaRequestType.ASSIGNABLE]: INITIAL_STATE_LABEL_ASSIGNABLE,
      [EpticaRequestType.DRAFT]: INITIAL_STATE_LABEL_DRAFT,
      [EpticaRequestType.SENT]: INITIAL_STATE_LABEL_SENT,
      [EpticaRequestType.DELEGATED]: INITIAL_STATE_LABEL_DELEGATED,
    },
  },
  queryFilters: copy(INITIAL_STATE_QUERY_FILTER),
  quickFilters: copy(INITIAL_STATE_QUICK_FILTER),
};

export enum QueryFilterKeys {
  HAS_FILTERS = 'hasFilters',
  SORT_KEY = 'sort',
  SORT_DIRECTION = 'order',
  SUBJECT = 'subject',
  DOMAIN = 'domain',
  SKILLS = 'skills',
  ATTACHMENT_ENABLED = 'attachmentEnabled',
  ATTACHMENT_ACTIVE = 'attachmentActive',
  PRIORITIES = 'priorities',
  BOOKMARK = 'bookmark',
  BEGIN_TIME = 'beginTime',
  END_TIME = 'endTime',
  IS_PERIOD_FILTER_ENABLED = 'isPeriodFilterEnabled',
  RECIPIENT = 'recipient',
  REASON = 'reason',
  REQUEST_ID = 'requestId',
}
